import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, ShoppingBag, Calendar, Package, User } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import TenantTheme from "../../lib/TenantTheme";
import CartRibbon from "../../components/CartRibbon";
import { configureStatusBar } from "../../lib/statusBar";
import { LoadingState } from "../../components/EmptyState";

export default function CustomerLayout() {
  const { user, tenant, logout, loading } = useAuth();
  const { t } = useT();
  const nav = useNavigate();

  // Native: make the WebView extend behind the status bar so its icons
  // render on top of the tenant's cream background. No-op on web.
  useEffect(() => { configureStatusBar({ bgColor: "#FAF9F6", style: "dark" }); }, []);

  if (loading) return <LoadingState fullHeight className="min-h-screen" />;
  // Allow GUEST browsing — only block non-customer logged-in users
  if (user && user.role !== "customer") {
    nav("/");
    return null;
  }

  const tabs = [
    { to: "/app/home", icon: Home, label: t("home") },
    { to: "/app/products", icon: ShoppingBag, label: t("shop") },
    { to: "/app/subscriptions", icon: Calendar, label: t("subs") },
    { to: "/app/orders", icon: Package, label: "Orders" },
    { to: "/app/profile", icon: User, label: t("me") },
  ];

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-brand-cream via-brand-cream to-neutral-100" data-testid="customer-app-layout">
      <TenantTheme tenant={tenant} />
      {/* Responsive shell — phone-shaped on mobile, expands to use real
          iPad / desktop screen real estate so App Store reviewers don't
          see a centered-phone-on-large-canvas (Apple flags those builds).
          Keep the same rounded card aesthetic on tablet by retaining the
          shadow + border at md+. */}
      <div className="mx-auto w-full max-w-md md:max-w-3xl lg:max-w-5xl xl:max-w-6xl md:shadow-2xl md:shadow-neutral-200 md:my-6 md:rounded-3xl md:bg-brand-cream md:border md:border-neutral-200">
        <Outlet context={{ user, tenant, logout }} />
        {/* Inside the same width-constrained column as the routed page so
            it lines up with that page's own content once it goes static
            below (see the comment on CartRibbon itself). */}
        <CartRibbon />
      </div>
      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md md:max-w-2xl bg-white border-t border-neutral-200 pb-safe pt-2 px-2 flex justify-around items-center h-20 z-50 rounded-t-3xl shadow-[0_-4px_20px_rgba(0,0,0,0.05)] md:rounded-3xl md:bottom-4 md:border md:shadow-xl"
           data-testid="customer-bottom-nav">
        {tabs.map((tab) => (
          <NavLink key={tab.to} to={tab.to}
                   className={({ isActive }) =>
                     `flex flex-col items-center justify-center min-w-[56px] py-2 rounded-2xl transition-colors ${isActive ? "text-primary" : "text-neutral-500"}`
                   }
                   data-testid={`nav-${tab.to.split("/").pop()}`}>
            {({ isActive }) => (
              <>
                <tab.icon className={`w-6 h-6 ${isActive ? "stroke-[2.5]" : ""}`} />
                <span className={`text-[10px] uppercase tracking-wider font-bold mt-0.5 ${isActive ? "" : "opacity-70"}`}>{tab.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
